import {
  applyTranslationsToContainer,
  getCurrentLanguage,
} from '../../translations.js';
import { showError } from '../../utils/UnifiedNotificationSystem.js';
import { setupCharacterCount, updateCount } from '../utils/validation.js';

export const FREE_VIDEO_PROMPT_MAX = 541;

const getT = (key, fallback) => {
  const lang = getCurrentLanguage() || 'en';
  const page = window.translations?.[lang]?.['create-video'] ?? window.translations?.en?.['create-video'];
  return page?.[key] ?? fallback;
};

const MODAL_ID = 'free-video-wand-modal';

const ensureModal = () => {
  const existing = document.getElementById(MODAL_ID);
  if (existing) return existing;

  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'modal ai-field-edit-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <div class="modal-content ai-field-modal-content" role="document" aria-labelledby="free-video-wand-title">
      <button type="button" class="close-btn" id="free-video-wand-close" data-translate-attr="aria-label:free_video_wand_close" aria-label="Close">×</button>
      <div class="ai-field-modal-body">
        <h3 id="free-video-wand-title" class="ai-field-modal-title" data-translate="free_video_wand_title">Describe the video</h3>
        <div id="free-video-wand-ask">
          <label class="form-field" for="free-video-wand-idea">
            <span class="form-label" data-translate="free_video_wand_idea_label">What do you want to see?</span>
            <textarea id="free-video-wand-idea" class="form-textarea form-textarea--tall form-textarea--taller" rows="4" data-translate-placeholder="free_video_wand_idea_placeholder" placeholder="She turns toward the camera and smiles."></textarea>
          </label>
          <div class="form-actions form-actions--right">
            <button type="button" class="btn btn-secondary" id="free-video-wand-cancel" data-translate="free_video_wand_cancel">Cancel</button>
            <button type="button" class="btn btn-primary" id="free-video-wand-generate" data-translate="free_video_wand_generate">Generate</button>
          </div>
        </div>
        <div id="free-video-wand-review" class="hidden">
          <label class="form-field" for="free-video-wand-draft">
            <span class="form-label" data-translate="free_video_wand_review_label">Review the prompt before using it</span>
            <textarea id="free-video-wand-draft" class="form-textarea form-textarea--tall form-textarea--taller" rows="8"></textarea>
            <div class="character-counter" id="free-video-wand-draft-counter" aria-live="polite">0/541</div>
          </label>
          <div class="form-actions form-actions--right">
            <button type="button" class="btn btn-secondary" id="free-video-wand-back" data-translate="free_video_wand_back">Back</button>
            <button type="button" class="btn btn-primary" id="free-video-wand-accept" data-translate="free_video_wand_accept">Use this prompt</button>
          </div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  setupCharacterCount('free-video-wand-draft', 'free-video-wand-draft-counter', FREE_VIDEO_PROMPT_MAX);
  return modal;
};

export const openFreeVideoWandModal = async ({ onGenerate }) => {
  const modal = ensureModal();
  applyTranslationsToContainer(modal, 'create-video', getCurrentLanguage());

  const ask = modal.querySelector('#free-video-wand-ask');
  const review = modal.querySelector('#free-video-wand-review');
  const idea = modal.querySelector('#free-video-wand-idea');
  const draft = modal.querySelector('#free-video-wand-draft');
  const generateBtn = modal.querySelector('#free-video-wand-generate');
  const closeBtn = modal.querySelector('#free-video-wand-close');
  const cancelBtn = modal.querySelector('#free-video-wand-cancel');
  const backBtn = modal.querySelector('#free-video-wand-back');
  const acceptBtn = modal.querySelector('#free-video-wand-accept');

  if (idea) idea.value = '';
  if (draft) draft.value = '';
  ask?.classList.remove('hidden');
  review?.classList.add('hidden');
  if (generateBtn) generateBtn.disabled = false;

  modal.classList.add('show');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
  if (idea instanceof HTMLTextAreaElement) setTimeout(() => idea.focus(), 0);

  const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  return await new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      modal.removeEventListener('click', handleBackdrop);
      document.removeEventListener('keydown', handleKeyDown, true);
      closeBtn?.removeEventListener('click', handleCancel);
      cancelBtn?.removeEventListener('click', handleCancel);
      generateBtn?.removeEventListener('click', handleGenerate);
      backBtn?.removeEventListener('click', handleBack);
      acceptBtn?.removeEventListener('click', handleAccept);
    };

    const close = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      previousActive?.focus();
      resolve(value);
    };

    const showAsk = () => {
      review?.classList.add('hidden');
      ask?.classList.remove('hidden');
      if (idea instanceof HTMLTextAreaElement) idea.focus();
    };

    const showReview = (text) => {
      if (draft) draft.value = text;
      const counter = modal.querySelector('#free-video-wand-draft-counter');
      if (draft instanceof HTMLTextAreaElement && counter instanceof HTMLElement) {
        updateCount(draft, counter, FREE_VIDEO_PROMPT_MAX);
      }
      ask?.classList.add('hidden');
      review?.classList.remove('hidden');
      if (draft instanceof HTMLTextAreaElement) draft.focus();
    };

    function handleCancel() { close(null); }

    function handleBackdrop(event) {
      if (event.target === modal) handleCancel();
    }

    function handleBack() { showAsk(); }

    async function handleGenerate() {
      const instruction = idea instanceof HTMLTextAreaElement ? idea.value.trim() : '';
      if (!instruction) return;
      if (generateBtn) generateBtn.disabled = true;
      try {
        const value = await onGenerate({ idea: instruction });
        const text = typeof value === 'string' ? value.trim() : '';
        if (!text) {
          showError(getT('free_video_wand_failed', 'Could not draft a video prompt'));
          return;
        }
        showReview(text);
      } catch (error) {
        if (error?.message !== 'QUOTA_HANDLED') {
          showError(error?.message || getT('free_video_wand_failed', 'Could not draft a video prompt'));
        }
      } finally {
        if (generateBtn) generateBtn.disabled = false;
      }
    }

    function handleAccept() {
      const text = draft instanceof HTMLTextAreaElement ? draft.value.trim() : '';
      if (!text) return;
      if (text.length > FREE_VIDEO_PROMPT_MAX) {
        showError(getT('free_video_prompt_too_long', 'Prompt is too long ({count}/{max} characters).')
          .replace('{count}', String(text.length))
          .replace('{max}', String(FREE_VIDEO_PROMPT_MAX)));
        return;
      }
      close({ prompt: text });
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    }

    closeBtn?.addEventListener('click', handleCancel);
    cancelBtn?.addEventListener('click', handleCancel);
    generateBtn?.addEventListener('click', () => {
      handleGenerate().catch((error) => {
        console.error('[freeVideoWand] generate failed', error);
      });
    });
    backBtn?.addEventListener('click', handleBack);
    acceptBtn?.addEventListener('click', handleAccept);
    modal.addEventListener('click', handleBackdrop);
    document.addEventListener('keydown', handleKeyDown, true);
  });
};
